import { formatDistanceToNow } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

import i18n from '../i18n';

// Presentation-only formatting. Values are stored and sent raw/normalized
// (tax IDs as uppercase alphanumerics, phones as E.164); these helpers only
// shape what the user sees. Two different axes:
// - dates/times/numbers follow the UI language (`i18n.language`);
// - documents (CNPJ/CPF) and postal codes follow the country of the data
//   (e.g. `persona.country`), and phones their own country calling code —
//   a Brazilian company keeps its CNPJ format when the UI is in English.

const EMPTY = '—';

// Placeholder for a missing value outside React components (they use
// `t('notAvailable')`); the text lives in the locales (`notAvailable`).
export const notAvailable = () => i18n.t('notAvailable');

const INTL_LOCALES = { pt: 'pt-BR', en: 'en-US', es: 'es' };
const DATE_FNS_LOCALES = { pt: ptBR, en: enUS, es };

// i18n language ('pt', 'pt-BR', 'en'...) -> BCP 47 locale for Intl.
export const toIntlLocale = (language) => {
  const base = (language || 'pt').split('-')[0];
  return INTL_LOCALES[base] ?? language;
};

export const getDateFnsLocale = (language) => DATE_FNS_LOCALES[(language || 'pt').split('-')[0]] ?? ptBR;

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWith = (value, language, options, empty) => {
  const date = toDate(value);
  if (!date) return empty;
  return new Intl.DateTimeFormat(toIntlLocale(language), options).format(date);
};

// `options` are extra Intl.DateTimeFormat options (e.g. { timeZone: 'UTC' }
// for plain `yyyy-MM-dd` dates); `empty` is what a missing value renders as.
export const formatDate = (value, language, { empty = EMPTY, ...options } = {}) =>
  formatWith(value, language, { day: '2-digit', month: '2-digit', year: 'numeric', ...options }, empty);

export const formatDateTime = (value, language, { empty = EMPTY, ...options } = {}) =>
  formatWith(value, language, { dateStyle: 'short', timeStyle: 'short', ...options }, empty);

export const formatTime = (value, language, { empty = EMPTY, ...options } = {}) =>
  formatWith(value, language, { hour: '2-digit', minute: '2-digit', ...options }, empty);

// "há 2 dias" / "2 days ago" / "hace 2 días".
export const formatRelative = (value, language, { empty = EMPTY } = {}) => {
  const date = toDate(value);
  if (!date) return empty;
  return formatDistanceToNow(date, { addSuffix: true, locale: getDateFnsLocale(language) });
};

// ---------------------------------------------------------------------------
// Documents and postal codes (by the data's country)
// ---------------------------------------------------------------------------

const alphanumeric = (value) => (value ?? '').toString().replace(/[^0-9a-z]/gi, '').toUpperCase();
const digits = (value) => (value ?? '').toString().replace(/\D/g, '');

// react-imask patterns per country. `A` is a CNPJ character: the new
// alphanumeric CNPJ (2026) allows letters in the first 12 positions; the
// 2 check digits stay numeric.
export const MASK_DEFINITIONS = { A: /[0-9A-Za-z]/ };
// Company tax IDs only (persona type company): CNPJ. Never a dynamic
// CPF/CNPJ mask — it can pick the shorter one and truncate the value.
export const TAX_ID_MASKS = {
  BR: 'AA.AAA.AAA/AAAA-00',
};
export const POSTAL_CODE_MASKS = {
  BR: '00000-000',
};

// Countries whose postal code identifies the address (backend
// LOOKUP_ADAPTERS): the address forms look these up to autofill.
const POSTAL_CODE_LOOKUP_LENGTHS = { BR: 8 };

export const canLookupPostalCode = (country, code) => {
  const length = POSTAL_CODE_LOOKUP_LENGTHS[(country || '').toUpperCase()];
  return Boolean(length) && digits(code).length === length;
};

// Accent/case-insensitive key to match names across sources ("Sao Paulo",
// "SÃO PAULO" and IBGE's "São Paulo" are the same city).
export const matchKey = (value) =>
  (value ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^0-9a-z]/gi, '')
    .toLowerCase();

// Legacy personas may have no country: the product is Brazil-first.
const countryOf = (country) => (country || 'BR').toUpperCase();

const CNPJ_LENGTH = 14;
const CPF_LENGTH = 11;
const CEP_LENGTH = 8;

export const formatTaxId = (value, country) => {
  const raw = alphanumeric(value);
  if (!raw) return value ? String(value) : '';
  if (countryOf(country) !== 'BR') return String(value);
  if (raw.length === CNPJ_LENGTH) {
    return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12)}`;
  }
  if (raw.length === CPF_LENGTH && /^\d+$/.test(raw)) {
    return `${raw.slice(0, 3)}.${raw.slice(3, 6)}.${raw.slice(6, 9)}-${raw.slice(9)}`;
  }
  return String(value);
};

export const formatPostalCode = (value, country) => {
  if (!value) return '';
  const raw = digits(value);
  if (countryOf(country) === 'BR' && raw.length === CEP_LENGTH) {
    return `${raw.slice(0, 5)}-${raw.slice(5)}`;
  }
  return String(value);
};

// ---------------------------------------------------------------------------
// Phones (by their own country calling code)
// ---------------------------------------------------------------------------

// Numbers without "+" are local to this country (input) — WhatsApp contact
// numbers come as bare international digits instead (see formatPhone).
export const DEFAULT_PHONE_COUNTRY = 'BR';

/**
 * "+5511998765432" / "5511998765432" -> "+55 11 99876-5432". Anything that
 * isn't a plain number (e.g. a phone masked with "*" for collaborators
 * without `conversation.contact.view_sensitive`) or doesn't parse is
 * returned untouched.
 */
export const formatPhone = (value) => {
  if (!value) return value ?? '';
  const text = String(value).trim();
  if (!/^\+?[\d\s().-]+$/.test(text)) return text;
  const international = text.startsWith('+') ? text : `+${digits(text)}`;
  const phone = parsePhoneNumberFromString(international);
  return phone?.isPossible() ? phone.formatInternational() : text;
};

// As-you-type display of a phone input; once the number is complete it gets
// the same format as formatPhone ("+55 11 99876-5432").
export const formatPhoneInput = (value) => {
  if (!value) return '';
  const typer = new AsYouType(DEFAULT_PHONE_COUNTRY);
  const text = typer.input(value);
  const phone = typer.getNumber();
  return phone?.isPossible() ? phone.formatInternational() : text;
};

// What a phone input sends: E.164 when it parses, the trimmed text otherwise
// (the backend has the final say).
export const toE164 = (value) => {
  const text = (value ?? '').trim();
  if (!text) return '';
  const phone = parsePhoneNumberFromString(text, DEFAULT_PHONE_COUNTRY);
  return phone?.isPossible() ? phone.number : text;
};

const phoneChars = (value) => {
  const text = (value ?? '').trim();
  return (text.startsWith('+') ? '+' : '') + digits(text);
};

/**
 * Next display value of a phone input. Deleting a separator (space, dash,
 * parenthesis) would just be re-added by the formatter, so a deletion that
 * kept the same digits drops the last digit instead.
 */
export const nextPhoneInput = (previous, next) => {
  const nextChars = phoneChars(next);
  if ((next ?? '').length < (previous ?? '').length && nextChars === phoneChars(previous)) {
    return formatPhoneInput(nextChars.slice(0, -1));
  }
  return formatPhoneInput(nextChars);
};
